import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ClipboardPlus,
  FileText,
  Megaphone,
  PenLine,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useSession } from "@/lib/auth-client";
import { useFlhaLabel } from "@/hooks/use-flha-label";
import { getComingSoonPaths } from "@/components/home/quick-action-grid";

export const Route = createFileRoute("/_authenticated/safety-tasks")({
  component: SafetyTasksPage,
});

interface Task {
  key: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  to: string;
  /** Always open, whatever the Coming Soon list says. */
  live?: boolean;
  badge?: number;
  /** Starting an FLHA is not open to crew yet: Coming Soon for everybody but super admins. */
  startsFlha?: boolean;
}

/** One place for everything a crew member does for safety: checks, toolbox talks, reports, signatures. */
function SafetyTasksPage() {
  const { data: session } = useSession();
  const role = session?.user?.role;
  const flha = useFlhaLabel();

  const { data: tb } = useQuery({
    queryKey: ["toolbox", "safety-tasks"],
    queryFn: () => apiGet<{ canCreate?: boolean }>("/api/toolbox?status=collecting"),
    staleTime: 60_000,
  });
  const { data: tbAwaiting } = useQuery({
    queryKey: ["toolbox", "awaiting"],
    queryFn: () => apiGet<{ data: { id: string }[] }>("/api/toolbox/awaiting"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const { data: draftData } = useQuery({
    queryKey: ["flha", "awaiting-my-signature"],
    queryFn: () => apiGet<{ total: number }>("/api/flha?status=draft&limit=0"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // Crew who were given access to FLHAs on a project (for now) can start one
  const { data: flhaAccess } = useQuery({
    queryKey: ["delegations", "mine", "flha"],
    queryFn: () => apiGet<{ data: { projectId: string }[] }>("/api/delegations/mine?kind=flha"),
    staleTime: 60_000,
  });
  const hasFlhaAccess = (flhaAccess?.data.length ?? 0) > 0;
  const waiting = (tbAwaiting?.data.length ?? 0) + (draftData?.total ?? 0);

  const tasks: Task[] = [
    { key: "flha", label: `Start new ${flha.singular}`, subtitle: `Check the job before you start`, icon: ClipboardPlus, to: "/flha/new-v2", startsFlha: true },
    { key: "my-flhas", label: flha.myLabel, subtitle: "Drafts and past submissions", icon: FileText, to: "/my-flhas", live: role === "subcontractor" },
    ...(tb?.canCreate ? [{ key: "toolbox-new", label: "Start a toolbox talk", subtitle: "Run the meeting, collect signatures", icon: Megaphone, to: "/toolbox", live: true } as Task] : []),
    { key: "toolbox-library", label: "Toolbox talks library", subtitle: "Read held talks", icon: Megaphone, to: "/toolbox/library", live: true },
    { key: "to-sign", label: "Awaiting my signature", subtitle: waiting > 0 ? `${waiting} waiting` : "All signed", icon: PenLine, to: "/my-signatures", live: true, badge: waiting },
    { key: "hazard", label: "Report a hazard", subtitle: "Flag a risk", icon: AlertTriangle, to: "/hazards/new" },
    { key: "incident", label: "Report an incident", subtitle: "File a report", icon: ShieldAlert, to: "/incidents" },
  ];

  const csPaths = getComingSoonPaths(role);

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-10">
      <div className="pt-2">
        <h1 className="text-xl font-bold text-text-primary">Safety Tasks</h1>
        <p className="text-sm text-muted-foreground">Everything you do for safety, in one place.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {tasks.map((t) => {
          const soon = t.startsFlha ? role !== "admin" && !hasFlhaAccess : !t.live && csPaths.has(t.to.split("?")[0]);
          const Icon = t.icon;
          const card = (
            <div
              className={cn(
                "relative flex h-full min-h-[104px] flex-col justify-between rounded-xl border bg-card p-3.5 shadow-card transition-all duration-150",
                soon ? "border-border-subtle" : "border-border-subtle hover:border-border-strong active:scale-[0.98] active:bg-surface-hover",
                t.key === "to-sign" && (t.badge ? "border-2 !border-red-600 !bg-red-100 dark:!bg-red-950/50" : "border-2 !border-emerald-600 !bg-emerald-100 dark:!bg-emerald-950/50")
              )}
            >
              <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", soon ? "bg-slate-100 text-slate-400 dark:bg-slate-800" : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200")}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-2">
                <p className={cn("text-[14px] font-semibold leading-tight", soon ? "text-text-tertiary" : "text-text-primary")}>{t.label}</p>
                <p className="mt-0.5 text-[11px] text-text-tertiary">{t.subtitle}</p>
              </div>
              {soon && <span className="absolute right-2 top-2 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold leading-none text-emerald-600 dark:text-emerald-400">Soon</span>}
              {!soon && !!t.badge && <span className="absolute right-3 top-3 min-w-[20px] rounded-full bg-red-600 px-1.5 text-center text-[10px] font-bold text-white">{t.badge > 99 ? "99+" : t.badge}</span>}
            </div>
          );
          return soon ? <div key={t.key}>{card}</div> : <Link key={t.key} to={t.to} className="block">{card}</Link>;
        })}
      </div>
    </div>
  );
}
